/**
 * Somvera Atelier — scroll reveal and anatomy hotspots.
 *
 * Dependency-free by design. GSAP would add ~50KB to a store whose visitors
 * are mostly on phones, for an effect that IntersectionObserver already does.
 *
 * The reveal only ever runs when motion is allowed. With reduced motion
 * requested, elements keep their default `opacity: 1` and this file adds no
 * classes at all — the reveal must never be the thing that makes content
 * readable.
 */

(function () {
  'use strict';

  const motionOK =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: no-preference)').matches
      : false;

  /* ---------------------------------------------------------------- reveal */

  function initReveal(root) {
    if (!motionOK || !('IntersectionObserver' in window)) return;

    const targets = (root || document).querySelectorAll('.sv-rv:not([data-sv-observed])');
    if (!targets.length) return;

    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    // Stagger within a group of four, then reset — a long list should not end
    // up with a two-second delay on its last item.
    //
    // The count runs per section, not across the document. Indexing the flat
    // NodeList meant an element's delay depended on how many revealed elements
    // happened to precede it in *other* sections, so two items arriving
    // together inside one section could be handed 180ms and 0ms and appear to
    // come in backwards. Sections also enter the viewport one at a time, which
    // is exactly the boundary the reset belongs on.
    const counts = new Map();

    targets.forEach(function (el) {
      el.setAttribute('data-sv-observed', '');

      const owner = el.closest('.shopify-section') || document.body;
      const index = counts.get(owner) || 0;
      counts.set(owner, index + 1);

      el.style.transitionDelay = (index % 4) * 60 + 'ms';
      io.observe(el);
    });
  }

  /* -------------------------------------------------------------- hotspots */

  function initHotspots(root) {
    const sections = (root || document).querySelectorAll('[data-sv-anatomy]');

    sections.forEach(function (section) {
      if (section.hasAttribute('data-sv-bound')) return;
      section.setAttribute('data-sv-bound', '');

      const spots = section.querySelectorAll('.sv-hotspot');
      const rows = section.querySelectorAll('.sv-anat__list li');
      if (!spots.length) return;

      function select(id) {
        spots.forEach(function (s) {
          s.setAttribute('aria-selected', String(s.dataset.hotspot === id));
        });
        rows.forEach(function (r) {
          r.dataset.on = String(r.dataset.hotspot === id);
        });
      }

      spots.forEach(function (s) {
        s.addEventListener('click', function () {
          select(s.dataset.hotspot);
        });
      });

      rows.forEach(function (r) {
        const btn = r.querySelector('.sv-anat__btn');
        if (btn) {
          btn.addEventListener('click', function () {
            select(r.dataset.hotspot);
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------ boot */

  function boot(root) {
    initReveal(root);
    initHotspots(root);
    attachHeroLoop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // Theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  /* ------------------------------------------------------------ hero loop */

  // The hero loop plays once each time it becomes roughly half visible, and
  // never attaches at all when the visitor has asked for reduced motion —
  // a CSS-only hide would still download and decode it. The reduced-motion
  // guard must run before the observer is ever created, so a visitor who
  // never scrolls the hero into view — and one who has asked for reduced
  // motion — never fetches the clip.
  function attachHeroLoop() {
    const video = document.querySelector('[data-sv-loop]:not([data-sv-loop-bound])');
    if (!video) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    video.setAttribute('data-sv-loop-bound', '');

    const source = video.dataset.svLoopSrc;
    if (!source) return;

    const wrapper = video.closest('.sv-hero-loop');
    let attached = false;

    function play() {
      video.play().then(
        function () {
          if (wrapper) wrapper.classList.add('is-playing');
        },
        function () {}
      );
    }

    // Plays once per entry into view rather than once per page view. The
    // held final frame reads as the still, so is-playing is never removed
    // on `ended` — the video stays visible over the poster. Re-entering
    // view after it finished rewinds to the first frame and replays; the
    // observer is left connected so re-entry can keep retriggering it.
    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (!attached) {
              attached = true;
              video.src = source;
            } else if (video.ended) {
              video.currentTime = 0;
            }
            play();
          } else if (attached) {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    io.observe(video);
  }
})();
